import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SaveButton from '../components/SaveButton';
import ContactButton from '../components/ContactButton';
import { Button } from '../components/ui/button';
import { useAuth } from '../App';
import { toast } from 'sonner';
import {
  ShoppingBag,
  MapPin,
  Mail,
  Phone,
  MessageCircle,
  Heart,
  Share2,
  ChevronLeft,
  Package,
  Tag,
  User,
  Clock,
  Eye,
  Check,
  Loader2,
  Edit,
  Trash2
} from 'lucide-react';

const API = ''; // Use relative URL for same-origin API calls

const ListingDetailPage = () => {
  const { listingId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState(0);

  useEffect(() => {
    fetchListing();
  }, [listingId]);

  const fetchListing = async () => {
    try {
      const res = await axios.get(`${API}/api/marketplace/${listingId}`);
      setListing(res.data);
    } catch (error) {
      toast.error('Listing not found');
      navigate('/marketplace');
    } finally {
      setLoading(false);
    }
  };

  const getConditionColor = (condition) => {
    switch (condition) {
      case 'New': return 'bg-green-100 text-green-700';
      case 'Like New': return 'bg-blue-100 text-blue-700';
      case 'Good': return 'bg-amber-100 text-amber-700';
      case 'Fair': return 'bg-orange-100 text-orange-700';
      case 'For Parts': return 'bg-red-100 text-red-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const handleContact = (method) => {
    if (!user) {
      toast.error('Please sign in to contact the seller');
      return;
    }
    if (method === 'email' && listing.contact_email) {
      window.location.href = `mailto:${listing.contact_email}?subject=Interested in: ${listing.title}`;
    } else if (method === 'whatsapp' && listing.contact_whatsapp) {
      window.open(`https://wa.me/${listing.contact_whatsapp.replace(/\D/g, '')}?text=Hi, I'm interested in: ${listing.title}`, '_blank');
    } else if (method === 'phone' && listing.contact_phone) {
      window.location.href = `tel:${listing.contact_phone}`;
    }
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </>
    );
  }

  if (!listing) return null;

  const isOwner = user && (listing.is_owner || listing.seller_id === user.user_id || listing.poster_user_id === user.user_id);
  const canEdit = listing.can_edit || isOwner;

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this listing? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/api/marketplace/${listingId}`);
      toast.success('Listing deleted');
      navigate('/marketplace');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete listing');
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-4 flex items-center justify-between">
            <Link 
              to="/marketplace" 
              className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
              Back to Marketplace
            </Link>
            {isOwner && (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate(`/marketplace/${listingId}/edit`)} data-testid="edit-listing-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit
                </Button>
                <Button variant="destructive" size="sm" onClick={handleDelete} data-testid="delete-listing-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Owner Actions Banner */}
        {canEdit && (
          <div className="bg-primary/5 border border-primary/20 px-4 py-3">
            <div className="container mx-auto flex items-center justify-between">
              <span className="text-sm font-medium text-primary">This is your listing</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={() => navigate(`/marketplace/${listingId}/edit`)} data-testid="edit-listing-banner-btn">
                  <Edit className="w-4 h-4 mr-1" /> Edit Listing
                </Button>
                <Button size="sm" variant="destructive" onClick={handleDelete} data-testid="delete-listing-banner-btn">
                  <Trash2 className="w-4 h-4 mr-1" /> Delete
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="container mx-auto px-4 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Images */}
            <div className="space-y-4">
              {/* Main Image */}
              <div className="relative aspect-square bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl overflow-hidden border-2 border-border">
                {listing.images?.length > 0 ? (
                  <img 
                    src={listing.images[selectedImage]} 
                    alt={listing.title}
                    className="w-full h-full object-contain bg-white"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Package className="w-24 h-24 text-primary/40" />
                  </div>
                )}
                {/* Badges */}
                <div className="absolute top-4 left-4 flex gap-2">
                  {listing.is_free && (
                    <span className="px-4 py-2 rounded-full text-sm font-bold bg-green-500 text-white">
                      FREE
                    </span>
                  )}
                  <span className={`px-4 py-2 rounded-full text-sm font-bold ${getConditionColor(listing.condition)}`}>
                    {listing.condition}
                  </span>
                </div>
                {/* Action Buttons */}
                <div className="absolute top-4 right-4 flex gap-2">
                  <SaveButton 
                    itemType="listing" 
                    itemId={listing.listing_id}
                    variant="ghost"
                    className="p-3 bg-white/90 rounded-full hover:bg-white transition-colors"
                    showText={false}
                  />
                  <button className="p-3 bg-white/90 rounded-full hover:bg-white transition-colors">
                    <Share2 className="w-5 h-5 text-muted-foreground" />
                  </button>
                </div>
              </div>
              {/* Thumbnail Gallery */}
              {listing.images?.length > 1 && (
                <div className="flex gap-2 overflow-x-auto">
                  {listing.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSelectedImage(idx)}
                      className={`w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 border-2 ${
                        selectedImage === idx ? 'border-primary' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Right Column - Details */}
            <div className="space-y-6">
              {/* Price & Title */}
              <div className="bg-white rounded-2xl border-2 border-border p-6">
                <div className="flex items-center gap-2 mb-3">
                  <span className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium">
                    {listing.category}
                  </span>
                </div>
                
                <h1 className="text-2xl md:text-3xl font-black mb-2">{listing.title}</h1>
                
                <div className="flex items-baseline gap-3 mb-4">
                  {listing.is_free ? (
                    <span className="text-4xl font-black text-green-600">FREE</span>
                  ) : (
                    <>
                      <span className="text-4xl font-black text-primary">
                        {new Intl.NumberFormat('en-US', {
                          style: 'currency',
                          currency: listing.price_currency || 'USD',
                          maximumFractionDigits: 0
                        }).format(listing.price)}
                      </span>
                      {listing.is_negotiable && (
                        <span className="text-muted-foreground">(Negotiable)</span>
                      )}
                    </>
                  )}
                </div>

                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <MapPin className="w-4 h-4" />
                    <span>{listing.district ? `${listing.district}, ` : ''}{listing.city}, {listing.country}</span>
                  </div>
                </div>
              </div>

              {/* Description */}
              <div className="bg-white rounded-2xl border-2 border-border p-6">
                <h2 className="text-xl font-bold mb-3">Description</h2>
                <p className="text-muted-foreground whitespace-pre-line">{listing.description}</p>
              </div>

              {/* Contact Card */}
              <div className="bg-white rounded-2xl border-2 border-border p-6">
                <h2 className="text-xl font-bold mb-4">Contact Seller</h2>
                
                {/* Seller Info */}
                <div className="flex items-center gap-3 mb-4 pb-4 border-b">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <div className="font-bold">{listing.contact_name || listing.seller_name}</div>
                    <div className="text-sm text-muted-foreground">Seller</div>
                  </div>
                </div>

                {/* Contact Buttons */}
                <div className="space-y-3">
                  {/* In-app messaging button */}
                  {listing.seller_id && (
                    <ContactButton
                      recipientId={listing.seller_id}
                      recipientName={listing.contact_name || listing.seller_name}
                      contextType="listing"
                      contextId={listing.listing_id}
                      contextTitle={listing.title}
                      className="w-full font-bold rounded-xl h-12 bg-teal-600 hover:bg-teal-700"
                    />
                  )}
                  {listing.contact_email && (
                    <Button 
                      variant="outline"
                      className="w-full font-bold rounded-xl h-12"
                      onClick={() => handleContact('email')}
                    >
                      <Mail className="w-5 h-5 mr-2" />
                      Email Seller
                    </Button>
                  )}
                  {listing.contact_whatsapp && (
                    <Button 
                      variant="outline"
                      className="w-full font-bold rounded-xl h-12 border-green-500 text-green-600 hover:bg-green-50"
                      onClick={() => handleContact('whatsapp')}
                    >
                      <MessageCircle className="w-5 h-5 mr-2" />
                      WhatsApp
                    </Button>
                  )}
                  {listing.contact_phone && (
                    <Button 
                      variant="outline"
                      className="w-full font-bold rounded-xl h-12"
                      onClick={() => handleContact('phone')}
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Call {listing.contact_phone}
                    </Button>
                  )}
                </div>

                {/* Stats */}
                <div className="mt-4 pt-4 border-t flex justify-between text-sm text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Eye className="w-4 h-4" />
                    {listing.views || 0} views
                  </div>
                  <div className="flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    {new Date(listing.created_at).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Safety Tips */}
              <div className="bg-amber-50 rounded-2xl p-4 border border-amber-200">
                <h3 className="font-bold text-amber-800 mb-2">Safety Tips</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Meet in a public place for the exchange
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Inspect the item before paying
                  </li>
                  <li className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    Use secure payment methods when possible
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ListingDetailPage;
